/**
 * 我的资源 → 展业城市（CLAUDE.md §前端 UI 须借鉴 LEO v1.0；本页复用 leo-* 类）。
 *
 * 行为：
 *   - 进入页面：GET /api/v1/legal-business-regions 拉取跨平台共享展业地域维表全表。
 *   - 市级城市：ChinaCityMap 只读可视化（橙色=展业城市；悬停蓝色 + tooltip 展示地域id）。
 *   - 省级区域（geo 上无市级面匹配的键）：以只读列表方式展示在地图下方。
 *   - 本页只读：展业地域维护不在应用侧（维表 auto_ad_legal_business_regions
 *     由 DBA / 后续维护入口更新），页面不提供添加 / 删除 / 编辑。
 */

import { useEffect, useMemo, useState } from "react";
import { ApiError } from "../../services/apiClient";
import {
  type LegalBusinessRegion,
  getLegalBusinessRegions,
} from "../../services/common/legalBusinessRegion";
import { ChinaCityMap } from "../../components/myResources/ChinaCityMap";
import { pushToast } from "../../hooks/useToast";

export default function BusinessCityPage() {
  const [regions, setRegions] = useState<LegalBusinessRegion[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [unmatched, setUnmatched] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await getLegalBusinessRegions();
        if (!cancelled) setRegions(rows);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof ApiError ? err.message : "拉取展业地域维表失败";
        pushToast(msg, "error");
        setRegions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // useMemo 保证 configured 引用仅在数据变化时改变（避免 ChinaCityMap effect 抖动）。
  const configured = useMemo(() => {
    const map: Record<string, number[]> = {};
    for (const r of regions ?? []) {
      map[r.region_name] = r.ocean_id;
    }
    return map;
  }, [regions]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-[13px] text-text-muted">加载中…</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-7 md:px-10 md:py-9 lg:px-14">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.55rem] font-bold leading-tight text-text-emphasis">
            展业城市
          </h1>
          <p className="mt-1 text-[12.5px] text-text-muted">
            跨平台共享展业地域维表（auto_ad_legal_business_regions，巨量 / Apple Ads
            共用）只读展示；地域维护不在本页。
          </p>
        </div>
        <div className="flex items-center gap-2">
          {regions && regions.length > 0 ? (
            <span className="leo-tag is-info">已配置 · {regions.length} 个地域</span>
          ) : (
            <span className="leo-tag is-warning">维表为空</span>
          )}
        </div>
      </div>

      {regions && regions.length > 0 ? (
        <>
          <div className="rounded-xl border border-line-strong bg-da-card p-5 shadow-card">
            <ChinaCityMap configured={configured} onUnmatched={setUnmatched} />
            <div className="text-[11px] text-text-muted">
              橙色=展业城市；悬停蓝色并展示地域id；地图仅展示，不可编辑（可滚轮缩放 / 拖拽平移）。
            </div>
          </div>

          {/* 省级展业区域（geo 上无市级面匹配的键），以只读列表展示在地图下方 */}
          {unmatched.length > 0 ? (
            <div className="mt-3 rounded-xl border border-line-strong bg-da-card p-5 shadow-card">
              <div className="mb-3 text-[12.5px] text-text-muted">
                省级展业区域（无市级地图面，共{" "}
                <span className="text-text-emphasis">{unmatched.length}</span> 个）：
              </div>
              <div className="space-y-1.5">
                {unmatched.map((name) => (
                  <div
                    key={name}
                    className="flex flex-wrap items-center gap-2 rounded-md border border-line-subtle bg-black/25 px-3 py-1.5"
                  >
                    <span className="leo-tag is-info">{name}</span>
                    <span className="font-mono text-[12px] text-text-muted">
                      地域id：[{(configured[name] ?? []).join(", ")}]
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="leo-card rounded-xl p-8 text-center text-[12.5px] text-text-muted">
          展业地域维表尚未初始化（auto_ad_legal_business_regions 为空）；维表为空时巨量广告地域红线校验跳过（fail-open）。
        </div>
      )}
    </div>
  );
}
